/**
 * ImageLightbox - Fullscreen photo viewer with pinch / double-tap zoom and pan.
 * Portaled like ModalShell; dismiss via backdrop, Close, or Escape.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 300;
const PAN_TAP_SLOP_PX = 10;

function distance(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

function ImageLightbox({ src, alt = '', onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const pinchStartDist = useRef(null);
  const pinchStartScale = useRef(1);
  const pinchStartOffset = useRef({ x: 0, y: 0 });
  const lastTapAt = useRef(0);
  const panStart = useRef(null);
  const didPan = useRef(false);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const clampScale = useCallback((value) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value)), []);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchStartDist.current = distance(e.touches[0], e.touches[1]);
      pinchStartScale.current = scaleRef.current;
      pinchStartOffset.current = { ...offsetRef.current };
      panStart.current = null;
      return;
    }
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const now = Date.now();
      if (now - lastTapAt.current < DOUBLE_TAP_MS) {
        if (scaleRef.current > MIN_SCALE) {
          resetView();
        } else {
          setScale(2);
          setOffset({ x: 0, y: 0 });
        }
        lastTapAt.current = 0;
        panStart.current = null;
        return;
      }
      lastTapAt.current = now;
      didPan.current = false;
      panStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        originX: offsetRef.current.x,
        originY: offsetRef.current.y,
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartDist.current) {
      e.preventDefault();
      const dist = distance(e.touches[0], e.touches[1]);
      const next = clampScale(pinchStartScale.current * (dist / pinchStartDist.current));
      setScale(next);
      if (next <= MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
      }
      return;
    }
    if (e.touches.length === 1 && panStart.current && scaleRef.current > MIN_SCALE) {
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - panStart.current.x;
      const dy = touch.clientY - panStart.current.y;
      if (Math.hypot(dx, dy) > PAN_TAP_SLOP_PX) {
        didPan.current = true;
        lastTapAt.current = 0;
      }
      setOffset({
        x: panStart.current.originX + dx,
        y: panStart.current.originY + dy,
      });
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchStartDist.current = null;
    }
    if (e.touches.length === 0) {
      panStart.current = null;
      if (scaleRef.current <= MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
      }
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-void/95"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      data-testid="image-lightbox"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end px-4 pt-safe-top">
        <button
          type="button"
          onClick={onClose}
          className="pointer-events-auto mt-2 rounded-xl bg-charcoal/80 px-3 py-2 text-2xl leading-none text-silver transition-zen hover:text-ash"
          aria-label="Close"
          data-testid="image-lightbox-close"
        >
          ×
        </button>
      </div>
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
        data-testid="image-lightbox-backdrop"
      />
      <div
        className="relative z-[1] flex h-full w-full touch-none items-center justify-center overflow-hidden p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          e.stopPropagation();
          if (!didPan.current && scaleRef.current <= MIN_SCALE) {
            // single tap on unzoomed image does not close; use backdrop/X
          }
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-h-[85dvh] max-w-full select-none object-contain"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
          data-testid="image-lightbox-img"
          data-scale={scale}
          data-offset-x={offset.x}
          data-offset-y={offset.y}
        />
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

export default ImageLightbox;
